<template>
    <div class="page">
        <EntityLandingHeader
            :background-image-url="backgroundImageUrl"
            :entity="city"
        />

        <section class="filters">
            <div class="filters__filter">
                <FxSelect
                    v-model="categoryKey"
                    :default-option="{ label: 'All Events', value: null }"
                    :disable-native="true"
                    :options="categoryOptions"
                    @input="onCategoryKeySelected"
                >
                    <span class="icon-chevron-lg-down" slot="caret"></span>
                </FxSelect>
            </div>
            <div class="filters__filter filters__filter--date">
                <span class="icon-calendar-blank filters__icon-calendar"></span>
                <fx-date-range
                    align="left"
                    data-e2e="search.date-filter"
                    min-date="today"
                    v-model="dateRange"
                    :custom-option-copy="'Choose Date'"
                    :default-option-copy="'Any Date'"
                    :presets="dateRangeFilterPresets"
                    @input="onDateRangeInput">
                    <span class="icon-chevron-lg-down" slot="caret"></span>
                </fx-date-range>
            </div>
        </section>

        <section>
            <EventList
                :events="events"
                :filters-applied="Boolean(categoryKey || dateRange)"
                :format-points="formatPoints"
                :is-loading-events="isLoadingEvents"
                :is-loading-more-events="isLoadingMoreEvents"
                :is-loyalty-pricing="$store.state.isRedeemingLoyaltyPoints"
                :load-more-copy="'Show More'"
                :loading-more-copy="'Loading More Events...'"
                :make-event-route="makeEventRoute"
                :on-event-click="onEventClick"
                :pagination="pagination"
                @load-more-events="onLoadMoreEvents"
            />
        </section>
    </div>
</template>

<script>
import { EVENT_NAME, CITY_LANDING_PAGE } from 'core/constants/googleAnalyticEvents'
import { getEntityLandingHeaderBackgroundUrl } from '../services/EntityService'
import DateUtil from 'core/utilities/DateUtil'
import EntityLandingHeader from '../components/EntityLandingHeader.vue'
import errorHandleable from '../mixins/ErrorHandleable'
import eventFilterable from '../mixins/EventFilterable'
import eventListable from '../mixins/EventListable'
import FxSelect from 'core/components/SelectComponent.vue'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import quebecUserModal from '../mixins/QuebecUserModal'
import trackable from '../mixins/Trackable'

export default {
    components: {
        EntityLandingHeader,
        FxSelect
    },
    computed: {
        backgroundImageUrl () {
            return getEntityLandingHeaderBackgroundUrl(this.city)
        },
        htmlPageTitle () {
            return this.city ? `${this.city.name} tickets at ${this.$config.partnerName}` : `City tickets at ${this.$config.partnerName}`
        }
    },
    created () {
        this.trackableEventCategory = 'UX - City Landing Page'

        this.categoryOptions = (this.$config.categories || [])
            .filter(category => Boolean(category.key)) // Filtering out null option
            .map(category => {
                return {
                    label: category.label,
                    value: category.key
                }
            })

        this.$marketplace
            .getCity(this.cityId)
            .then(city => {
                this.city = city
            })
            .catch(err => this.catchAndNotify(err))
            .finally(() => {
                this.trackPageView(this.$route.fullPath, this.htmlPageTitle)
                this.$emit('updateHead')
            })
    },
    data () {
        return {
            city: null
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    methods: {
        fetchEvents () {
            return this.$store.state.userLocationPromise
                .then(() => {
                    return this.$marketplace.getEntityEvents('cities', this.cityId, {
                        all_in_price: this.$config.isAllInPricing || this.$store.state.isDefaultToAllInPrice ? 1 : 0,
                        category: this.category ? this.category.key : null,
                        date_end: this.serializedEndDate,
                        date_start: this.serializedStartDate,
                        location_id: this.locationId,
                        opponent_id: this.opponentId,
                        page: this.page,
                        per_page: 10,
                        utc_offset: DateUtil.format(new Date(), 'Z'),
                        whereabouts: null
                    })
                })
                .then(response => {
                    return {
                        events: response.chronological.results,
                        pagination: response.chronological.meta
                    }
                })
        },
        onLoadMoreEvents () {
            this.$googleTagManager.pushEvent(EVENT_NAME, CITY_LANDING_PAGE.LOAD_MORE_EVENTS_SELECTED)
            this.loadMoreEvents()
        },
        onCategoryKeySelected (categoryKey) {
            this.$googleTagManager.pushEvent(EVENT_NAME, Object.assign({}, CITY_LANDING_PAGE.EVENT_TAXONOMY_FILTER_CLICKED, { value: categoryKey }))
            // TODO: Delete this event from GA
            this.trackClickEvent(`${categoryKey || 'all'} Genre Filter - Clicked`)
        },
        onDateRangeInput (dateRange) {
            this.$googleTagManager.pushEvent(EVENT_NAME, CITY_LANDING_PAGE.DATE_RANGE_FILTER_APPLIED)
            // TODO: Delete this event from GA
            this.trackClickEvent('Date Filter - Clicked')
            this.onDateRangeSelected(dateRange)
        },
        onEventClick () {
            this.$googleTagManager.pushEvent(EVENT_NAME, CITY_LANDING_PAGE.EVENT_SELECTED)
            // TODO: Delete this event from GA
            this.trackClickEvent('Select Event on City Page')
        }
    },
    mixins: [
        errorHandleable,
        eventFilterable,
        eventListable,
        loyaltyFormattable,
        quebecUserModal,
        trackable
    ],
    props: ['cityId']
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.filters {
    align-items: center;
    color: #808689;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 24px;
    -webkit-font-smoothing: antialiased;

    & .filters__icon-calendar {
        color: #808689;
        font-size: var(--fs-large);
        margin-right: 4px;
        position: relative;
        top: 6px;
    }

    & .filters__filter {
        padding: 0;
        align-self: flex-end;
        display: flex;
    }

    & >>> {
        & .fx-select-custom {
            padding: 0 14px 0 0;
        }

        & .fx-select-input {
            border-bottom: 0;
        }

        & .fx-selection {
            color: #808689;
            font-size: 16px;
            text-decoration: underline;
        }

        & .icon-cancel {
            color: #808689;
            font-size: 16px;
            position: relative;
            right: 3px;
            top: 4px;
        }

        & .icon-chevron-lg-down {
            color: #808689;
            font-size: 14px;
            position: relative;
            right: 3px;
            top: 3px;
        }
    }
}

.page {
    margin-top: -65px;
    padding: 0 var(--site-padding) 48px;
    width: 100%;

    @media (--mq-full-min) {
        margin: -100px auto 0;
        width: var(--site-container-width);
    }
}
</style>
